// =============================================================================
// useSubscripcion.ts
// Carga plan activo, plan efectivo (promo o contratado), todos los planes
// con servicios, comprobantes y el historial de movimientos.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

export interface PlanActivo {
  id_contratacion: string;
  id_plan: string;
  plan: string;
  nombre_plan: string;
  precio_mensual: number;
  precio_trabajador_adicional: number;
  trabajadores_incluidos: number;
  es_gratis: boolean;
  fecha_inicio: string;
  fecha_fin: string | null;
  auto_renovacion: boolean;
  es_fundador_diamante: boolean;
  es_fundador_platino: boolean;
  id_suscripcion_stripe: string | null;
  stripe_status: string | null;
  id_plan_pendiente: string | null;
  nombre_plan_pendiente: string | null;
  precio_mensual_pendiente: number | null;
  precio_trabajador_adicional_pendiente: number | null;
}

// Plan que manda AHORA MISMO en permisos, cupo y funciones. Durante una
// promoción que concede plan (p. ej. lanzamiento → Premium), el efectivo
// es el promocional aunque la empresa tenga contratado otro (o ninguno).
// Al terminar la promo, el acceso pasa al plan contratado — o a ninguno
// si nunca eligió.
export interface PlanEfectivo {
  id_empresa: string;
  // Condición de fundador: de la empresa, no del plan
  es_fundador_diamante: boolean;
  es_fundador_platino: boolean;
  id_plan_efectivo: string | null;
  plan_efectivo: string | null;
  trabajadores_incluidos_efectivo: number;
  precio_trabajador_adicional_efectivo: number;
  por_promocion: boolean;
  fecha_fin_promocion: string | null;
  id_plan_contratado: string | null;
  plan_contratado: string | null;
  // Límites del plan CONTRATADO: son los que mandan para dar de alta
  // trabajadores, aunque la promo conceda un plan superior.
  trabajadores_incluidos_contratado: number;
  precio_trabajador_adicional_contratado: number;
  id_contratacion: string | null;
  fecha_fin_contratacion: string | null;
}

export interface ServicioPlan {
  id_plan: string;
  plan: string;
  orden_plan: number;
  precio_mensual: number;
  precio_trabajador_adicional: number;
  tiene_promocion: boolean;
  promocion_activa: boolean;
  precio_promocion: number | null;
  fecha_fin_promocion: string | null;
  es_gratis: boolean;
  descripcion_plan: string | null;
  id_servicio_saas: string;
  servicio_saas: string;
  descripcion_corta: string | null;
  orden_servicio: number;
  id_categoria_saas: string;
  categoria: string;
  orden_categoria: number;
}

export interface PlanAgrupado {
  id_plan: string;
  plan: string;
  orden_plan: number;
  precio_mensual: number;
  precio_trabajador_adicional: number;
  tiene_promocion: boolean;
  promocion_activa: boolean;
  precio_promocion: number | null;
  fecha_fin_promocion: string | null;
  es_gratis: boolean;
  descripcion_plan: string | null;
  categorias: {
    id_categoria_saas: string;
    categoria: string;
    orden_categoria: number;
    servicios: {
      id_servicio_saas: string;
      servicio_saas: string;
      descripcion_corta: string | null;
      orden_servicio: number;
    }[];
  }[];
  servicios_ids: Set<string>;
}

export interface Comprobante {
  id_comprobante: string;
  id_empresa: string;
  nombre_empresa: string;
  numero: string;
  periodo: string;
  fecha_emision: string;
  monto: number;
  concepto: string;
  activo: boolean;
  descripcion: string | null;
  observaciones: string | null;
  fecha_creacion: string;
  ruta_storage: string | null;
  url_factura_stripe: string | null;
}

// Historial unificado de movimientos (plan + trabajadores), procedente de
// vista_movimientos_suscripcion.
export interface MovimientoSuscripcion {
  id_empresa: string;
  ambito: 'plan' | 'trabajador';
  tipo_movimiento: string;
  fecha_movimiento: string;
  detalle: string | null; // nombre del plan (ámbito plan)
  trabajador: string | null; // nombre del trabajador (ámbito trabajador)
  fecha_inicio: string | null;
  fecha_fin: string | null;
  observaciones: string | null;
}

// =============================================================================
// Helpers
// =============================================================================

function agruparPlanes(rows: ServicioPlan[]): PlanAgrupado[] {
  const mapaPlanes = new Map<string, PlanAgrupado>();

  rows.forEach((row) => {
    if (!mapaPlanes.has(row.id_plan)) {
      mapaPlanes.set(row.id_plan, {
        id_plan: row.id_plan,
        plan: row.plan,
        orden_plan: row.orden_plan,
        precio_mensual: row.precio_mensual,
        precio_trabajador_adicional: row.precio_trabajador_adicional,
        tiene_promocion: row.tiene_promocion,
        promocion_activa: row.promocion_activa,
        precio_promocion: row.precio_promocion,
        fecha_fin_promocion: row.fecha_fin_promocion,
        es_gratis: row.es_gratis,
        descripcion_plan: row.descripcion_plan,
        categorias: [],
        servicios_ids: new Set(),
      });
    }

    const plan = mapaPlanes.get(row.id_plan)!;
    plan.servicios_ids.add(row.id_servicio_saas);

    let cat = plan.categorias.find(
      (c) => c.id_categoria_saas === row.id_categoria_saas
    );
    if (!cat) {
      cat = {
        id_categoria_saas: row.id_categoria_saas,
        categoria: row.categoria,
        orden_categoria: row.orden_categoria,
        servicios: [],
      };
      plan.categorias.push(cat);
    }

    if (
      !cat.servicios.find((s) => s.id_servicio_saas === row.id_servicio_saas)
    ) {
      cat.servicios.push({
        id_servicio_saas: row.id_servicio_saas,
        servicio_saas: row.servicio_saas,
        descripcion_corta: row.descripcion_corta,
        orden_servicio: row.orden_servicio,
      });
    }
  });

  return Array.from(mapaPlanes.values())
    .sort((a, b) => a.orden_plan - b.orden_plan)
    .map((p) => ({
      ...p,
      categorias: p.categorias
        .sort((a, b) => a.orden_categoria - b.orden_categoria)
        .map((c) => ({
          ...c,
          servicios: c.servicios.sort(
            (a, b) => a.orden_servicio - b.orden_servicio
          ),
        })),
    }));
}

function getPrecioFinal(plan: PlanAgrupado): number {
  const fechaFin = plan.fecha_fin_promocion
    ? new Date(plan.fecha_fin_promocion)
    : null;
  const promoValida =
    plan.promocion_activa &&
    typeof plan.precio_promocion === 'number' &&
    plan.precio_promocion < plan.precio_mensual &&
    fechaFin instanceof Date &&
    new Date() <= fechaFin;
  return promoValida ? (plan.precio_promocion as number) : plan.precio_mensual;
}

// =============================================================================
// Hook
// =============================================================================

export const useSubscripcion = (empresaId: string | undefined) => {
  const [planActivo, setPlanActivo] = useState<PlanActivo | null>(null);
  const [planEfectivo, setPlanEfectivo] = useState<PlanEfectivo | null>(null);
  const [planes, setPlanes] = useState<PlanAgrupado[]>([]);
  const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
  const [movimientos, setMovimientos] = useState<MovimientoSuscripcion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingComprobante] = useState(false);

  // ── 1. Plan activo (lo realmente contratado) ──────────────────────────────

  const fetchPlanActivo = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('vista_planes_empresa')
      .select('*')
      .eq('id_empresa', empresaId)
      .eq('activo', true)
      .maybeSingle();

    if (error) {
      console.error('Error cargando plan activo:', error);
      return;
    }
    if (data) {
      setPlanActivo({ ...data, plan: data.nombre_plan ?? data.plan ?? '' });
    }
  }, [empresaId]);

  // ── 2. Plan efectivo (el que manda ahora: promo o contratado) ─────────────
  // Durante la promoción de lanzamiento, todas las empresas tienen el plan
  // promocional (Premium) aunque hayan contratado otro o ninguno. Es lo que
  // debe gobernar cupo de trabajadores, funciones y lo que se muestra como
  // "Plan activo" en la interfaz.

  const fetchPlanEfectivo = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('vista_plan_efectivo_empresa')
      .select('*')
      .eq('id_empresa', empresaId)
      .maybeSingle();

    if (error) {
      console.error('Error cargando plan efectivo:', error);
      return;
    }
    setPlanEfectivo(data as PlanEfectivo);
  }, [empresaId]);

  // ── 3. Todos los planes con servicios ─────────────────────────────────────

  const fetchPlanes = useCallback(async () => {
    const { data, error } = await supabase
      .from('vista_planes_servicios')
      .select('*')
      .order('orden_plan', { ascending: true })
      .order('orden_categoria', { ascending: true })
      .order('orden_servicio', { ascending: true });

    if (error) {
      console.error('Error cargando planes:', error);
      return;
    }
    setPlanes(agruparPlanes((data ?? []) as ServicioPlan[]));
  }, []);

  // ── 4. Comprobantes ───────────────────────────────────────────────────────
  // Los comprobantes reales ahora los crea el webhook de Stripe
  // (evento invoice.paid) cuando se confirma un cobro de verdad — con
  // importe y concepto reales. Aquí solo se leen.

  const fetchComprobantes = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('vista_comprobantes')
      .select('*')
      .eq('id_empresa', empresaId)
      .order('fecha_emision', { ascending: false });

    if (error) {
      console.error('Error cargando comprobantes:', error);
      return;
    }
    setComprobantes(data ?? []);
  }, [empresaId]);

  // ── 5. Movimientos (plan + trabajadores) ──────────────────────────────────
  // Historial unificado de altas, bajas y cambios, tanto del plan como de
  // los trabajadores, desde vista_movimientos_suscripcion.

  const fetchMovimientos = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('vista_movimientos_suscripcion')
      .select('*')
      .eq('id_empresa', empresaId)
      .order('fecha_movimiento', { ascending: false });

    if (error) {
      console.error('Error cargando movimientos:', error);
      return;
    }
    setMovimientos((data ?? []) as MovimientoSuscripcion[]);
  }, [empresaId]);

  // ── Efecto principal ──────────────────────────────────────────────────────
  // NOTA: se ha eliminado la generación automática de comprobantes de 0€
  // por cada mes desde el registro ("Periodo de lanzamiento gratuito").
  // Era del sistema anterior, cuando todo era gratis por defecto. Ahora
  // que hay cobros reales vía Stripe, generar esos comprobantes falsos en
  // paralelo duplicaría y contaminaría el historial real de facturación.

  useEffect(() => {
    if (!empresaId) return;
    const init = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchPlanActivo(),
          fetchPlanEfectivo(),
          fetchPlanes(),
          fetchComprobantes(),
          fetchMovimientos(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [empresaId]);

  // ── Descargar factura/comprobante ───────────────────────────────────────
  // Solo se devuelve la factura real de Stripe (guardada en Storage al
  // procesar invoice.paid). Si un comprobante no tiene ruta_storage es
  // porque fue gratuito (sin cobro de por medio) y Stripe nunca generó
  // factura para él — no hay nada que descargar en ese caso.

  const descargarComprobante = useCallback(async (comprobante: Comprobante) => {
    if (!comprobante.ruta_storage) {
      toast.error('Este periodo fue gratuito, no hay factura de Stripe asociada.');
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('facturas')
        .createSignedUrl(comprobante.ruta_storage, 60);

      if (error || !data?.signedUrl) throw error ?? new Error('Sin URL firmada');

      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');

      toast.success('Factura abierta en una pestaña nueva');
    } catch (err) {
      console.error('Error abriendo factura de Storage:', err);
      toast.error('Error al abrir la factura');
    }
  }, []);

  return {
    planActivo,
    planEfectivo,
    planes,
    comprobantes,
    movimientos,
    loading,
    loadingComprobante,
    descargarComprobante,
    fetchComprobantes,
    fetchMovimientos,
    fetchPlanActivo,
    fetchPlanEfectivo,
    getPrecioFinal,
  };
};

export { getPrecioFinal };